<template>
  <div class="cut-order" :class="['cut-order--' + state.key, { 'cut-order--open': expanded }]">
    <div class="cut-order__head" role="button" tabindex="0" @click="toggle" @keydown.enter.self="toggle">
      <AppIcon :name="expanded ? 'expand_less' : 'expand_more'" class="cut-order__chevron" />
      <span class="cut-order__state" :title="state.text"><AppIcon :name="state.icon" :fill="state.key === 'ready' || state.key === 'invoiced'" /></span>
      <div class="cut-order__identity">
        <span class="cut-order__number">#{{ order.id }}</span>
        <span class="cut-order__customer">{{ order.customer_name }}</span>
        <span v-if="order.nd" class="cut-order__tag" title="Punto de venta no fiscal">ND</span>
      </div>

      <div class="cut-order__status">
        <span v-if="order.short" class="cut-order__tag cut-order__tag--danger">
          <AppIcon name="trending_down" /> {{ order.short }} {{ order.short === 1 ? 'faltante' : 'faltantes' }}
        </span>
        <span v-if="order.weighable" class="cut-order__tag" :class="order.weighable_pending ? 'cut-order__tag--warning' : 'cut-order__tag--success'">
          <AppIcon name="scale" />
          {{ order.weighable_pending ? order.weighable_pending + ' a pesar' : 'Pesado' }}
        </span>
        <span v-if="invoiceBadge" class="cut-order__tag" :class="invoiceBadge.class" :title="order.voucher || ''">
          <AppIcon :name="invoiceBadge.icon" /> {{ invoiceBadge.text }}
        </span>
        <span v-else-if="controlled" class="cut-order__tag cut-order__tag--success">
          <AppIcon name="verified" /> Controlado
        </span>
        <b-button v-else-if="cut.can.edit" ref="controlButton" size="sm" variant="outline-success" class="cut-order__control"
                  :disabled="busy || order.weighable_pending > 0"
                  :title="order.weighable_pending ? 'Faltan pesar ' + order.weighable_pending + ' productos' : 'Marcar el pedido como controlado'"
                  @click.stop="control">
          <AppIcon name="done_all" /> Controlar
        </b-button>
      </div>

      <div class="cut-order__actions" @click.stop>
        <b-dropdown v-if="canReopen || canRemove"
                    right no-caret size="sm" variant="link" boundary="window"
                    toggle-class="app-table-action-toggle">
          <template #button-content><AppIcon name="more_vert" label="Acciones del pedido" /></template>
          <b-dropdown-item v-if="canReopen" @click="$emit('reopen', order)">
            <AppIcon name="lock_open" /> Reabrir pedido para modificar
          </b-dropdown-item>
          <b-dropdown-item v-if="canRemove" @click="$emit('remove', order)">
            <AppIcon name="remove_shopping_cart" class="text-danger" /> Quitar del corte
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <div v-if="order.invoice_status === 'error' && order.invoice_error" class="cut-order__error">
      <AppIcon name="error" /> {{ order.invoice_error }}
    </div>

    <div v-if="expanded" class="cut-order__body">
      <div v-if="loading" class="cut-order__loading"><b-spinner small variant="secondary" /> Cargando productos…</div>
      <template v-else-if="lines">
        <div v-if="weighableLines && !locked" class="cut-order__weigh-hint">
          <AppIcon name="scale" />
          <span>
            <strong>{{ weighableLines }} {{ weighableLines === 1 ? 'producto pesable' : 'productos pesables' }}.</strong>
            Escribí el peso de la balanza en el campo amarillo y elegí kg o g. Se factura ese peso.
            Debajo de lo pedido tenés el estimado como referencia. Si no se entrega, cargá 0.
          </span>
        </div>

        <b-table class="app-table app-table--compact cut-order__lines mb-0"
                 :items="lines" :fields="fields" :tbody-tr-class="lineClass"
                 :stacked="isMobile ? 'md' : false" primary-key="detail_id" small responsive>
          <template v-slot:cell(product)="data">
            <span class="cut-order__code">{{ data.item.code }}</span>
            {{ data.item.description }}
            <span v-if="data.item.weighable" class="cut-order__tag cut-order__tag--warning"><AppIcon name="scale" /> Pesable</span>
            <div v-if="noteOpen[data.item.detail_id] || data.item.note" class="cut-order__note">
              <b-form-input v-model="forms[data.item.detail_id].note" size="sm" placeholder="Nota para el pedido"
                            :disabled="locked" @change="save(data.item)" />
            </div>
          </template>

          <template v-slot:cell(requested)="data">
            <div>{{ formatQuantity(data.item.quantity) }} {{ data.item.unit_reference }}</div>
            <small v-if="data.item.weighable && data.item.estimated_weight" class="cut-order__estimate">
              ≈ {{ formatQuantity(data.item.estimated_weight) }} kg
            </small>
            <small v-if="data.item.stock && data.item.stock.controlled" class="cut-order__stock" :class="'cut-order__stock--' + stockTone(data.item)"
                   :title="stockTitle(data.item)">
              <AppIcon :name="stockTone(data.item) === 'ok' ? 'inventory_2' : 'warning'" />
              Stock {{ formatQuantity(data.item.stock.available) }} {{ data.item.stock.unit }}
            </small>
          </template>

          <template v-slot:cell(prepared)="data">
            <div v-if="data.item.weighable" class="cut-order__input cut-order__input--weigh">
              <b-form-input :ref="'input-' + data.item.detail_id"
                            v-model="forms[data.item.detail_id].value"
                            type="number" min="0" step="0.001" size="sm" inputmode="decimal"
                            placeholder="Peso"
                            :class="{ 'cut-order__input--short': !!weightWarning(data.item) }"
                            :disabled="locked"
                            @focus.native="$event.target.select()"
                            @change="save(data.item)"
                            @keydown.enter.native.prevent="saveAndNext(data.item)" />
              <b-form-select v-model="forms[data.item.detail_id].weight_unit_id" :options="weightUnitOptions" size="sm"
                             class="cut-order__unit" :disabled="locked" @change="save(data.item)" />
            </div>
            <div v-else class="cut-order__input">
              <b-form-input :ref="'input-' + data.item.detail_id"
                            v-model="forms[data.item.detail_id].value"
                            type="number" min="0" :max="data.item.quantity" :step="data.item.integer ? 1 : 0.001" size="sm"
                            :inputmode="data.item.integer ? 'numeric' : 'decimal'"
                            :class="{ 'cut-order__input--short': isShort(data.item) || !!quantityError(data.item) }"
                            :disabled="locked"
                            @focus.native="$event.target.select()"
                            @change="save(data.item)"
                            @keydown.enter.native.prevent="saveAndNext(data.item)" />
              <span class="cut-order__unit-label">{{ data.item.unit_reference }}</span>
            </div>
            <small v-if="errors[data.item.detail_id]" class="cut-order__field-warning">
              <AppIcon name="error" /> {{ errors[data.item.detail_id] }}
            </small>
            <small v-else-if="quantityError(data.item)" class="cut-order__field-warning">
              <AppIcon name="warning" /> {{ quantityError(data.item) }}
            </small>
            <small v-else-if="data.item.weighable && weightWarning(data.item)" class="cut-order__field-warning">
              <AppIcon name="warning" /> {{ weightWarning(data.item) }}
            </small>
            <small v-else-if="shortWithStock(data.item)" class="cut-order__field-warning cut-order__field-warning--stock">
              <AppIcon name="search" /> El sistema indica {{ formatQuantity(data.item.stock.available) }} {{ data.item.stock.unit }}: buscalo antes de cargar el faltante.
            </small>
          </template>

          <template v-slot:cell(state)="data">
            <b-spinner v-if="saving[data.item.detail_id]" small variant="secondary" title="Guardando" />
            <b-button v-else-if="!locked && !noteOpen[data.item.detail_id] && !data.item.note"
                      variant="link" class="cut-order__note-btn" title="Agregar nota"
                      @click="$set(noteOpen, data.item.detail_id, true)">
              <AppIcon name="edit_note" />
            </b-button>
          </template>
        </b-table>

        <div v-if="cut.can.edit" class="cut-order__footer" :class="{ 'cut-order__footer--done': controlled }">
          <template v-if="controlled">
            <span><AppIcon name="verified" /> Pedido controlado. Las cantidades quedan bloqueadas.</span>
            <b-button variant="link" class="list-view-link p-0" @click="$emit('reopen', order)">
              <AppIcon name="lock_open" /> Reabrir para modificar
            </b-button>
          </template>
          <template v-else>
            <span v-if="order.weighable_pending"><AppIcon name="scale" /> Cargá el peso de {{ order.weighable_pending }} {{ order.weighable_pending === 1 ? 'producto' : 'productos' }} para poder controlar el pedido.</span>
            <span v-else>Corregí solo lo que falte; lo que no modifiques se toma con la cantidad pedida.</span>
            <b-button size="sm" variant="success" :disabled="busy || order.weighable_pending > 0" @click="control">
              <AppIcon name="done_all" /> Controlar pedido
            </b-button>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import serviceAPI from '../services'
import Error from '@/handler/error'

export default {
  name: 'OrderCutOrder',
  props: {
    cut: { type: Object, required: true },
    order: { type: Object, required: true },
    expanded: { type: Boolean, default: false },
    weightUnits: { type: Array, default: () => [] },
    revision: { type: Number, default: 0 },
    isMobile: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
  },
  data() {
    return {
      lines: null,
      loading: false,
      forms: {},
      saving: {},
      errors: {},
      noteOpen: {},
      confirming: false,
      fields: [
        { key: 'product', label: 'Producto' },
        { key: 'requested', label: 'Pedido', class: 'text-right cut-order__col-qty' },
        { key: 'prepared', label: 'Preparado / peso real', class: 'cut-order__col-prepared' },
        { key: 'state', label: '', class: 'cut-order__col-state' },
      ],
    }
  },
  computed: {
    controlled() { return this.order.lines > 0 && this.order.confirmed >= this.order.lines },
    // Un pedido controlado queda bloqueado: para corregirlo hay que reabrirlo a propósito.
    locked() { return !this.cut.can.edit || this.controlled },
    weightUnitOptions() { return this.weightUnits.map(unit => ({ value: unit.id, text: unit.reference.toLowerCase() })) },
    weighableLines() { return this.lines ? this.lines.filter(line => line.weighable).length : 0 },
    canReopen() { return this.cut.can.edit && this.controlled },
    canRemove() {
      return ['preparing', 'prepared'].includes(this.cut.status) && !this.order.has_sale &&
        !['done', 'pending', 'processing'].includes(this.order.invoice_status)
    },
    // Estado visual del pedido: color e ícono a la izquierda de la fila.
    state() {
      const order = this.order
      if(order.invoice_status === 'error') return { key: 'error', icon: 'error', text: 'Error al facturar' }
      if(order.invoice_status === 'done') return { key: 'invoiced', icon: 'receipt_long', text: 'Facturado' }
      if(['pending', 'processing'].includes(order.invoice_status)) return { key: 'running', icon: 'sync', text: 'Facturando' }
      if(this.controlled) return { key: 'ready', icon: 'verified', text: 'Controlado' }
      if(order.weighable_pending) return { key: 'partial', icon: 'scale', text: 'Falta pesar' }
      return { key: 'pending', icon: 'radio_button_unchecked', text: 'Sin controlar' }
    },
    invoiceBadge() {
      return {
        done: { text: this.order.voucher || 'Facturado', class: 'cut-order__tag--success', icon: 'receipt_long' },
        error: { text: 'Error al facturar', class: 'cut-order__tag--danger', icon: 'error' },
        pending: { text: 'En cola', class: 'cut-order__tag--info', icon: 'schedule' },
        processing: { text: 'Facturando', class: 'cut-order__tag--info', icon: 'sync' },
      }[this.order.invoice_status] || null
    },
  },
  watch: {
    expanded(value) { if(value && !this.lines) this.load() },
    // Un cambio masivo (controlar todo, reabrir) invalida los renglones ya cargados.
    revision() {
      if(this.expanded) this.load()
      else this.lines = null
    },
  },
  created() { if(this.expanded) this.load() },
  methods: {
    toggle() { this.$emit('toggle', this.order.id) },
    async load() {
      this.loading = true
      try {
        const response = await serviceAPI.renglonesPedidoCorte(this.cut.id, this.order.id)
        this.setLines(response.data.lines || [])
      } catch(error) {
        this.$awn.alert(Error.showError(error))
      } finally {
        this.loading = false
      }
    },
    setLines(lines) {
      const forms = {}
      lines.forEach(line => { forms[line.detail_id] = this.formFor(line) })
      this.forms = forms
      this.errors = {}
      this.lines = lines
    },
    formFor(line) {
      const defaultWeightUnit = this.weightUnits.length ? this.weightUnits[0].id : null
      if(line.weighable) {
        return {
          value: line.measured_weight !== null ? line.measured_weight : '',
          weight_unit_id: line.weight_unit_id || defaultWeightUnit,
          note: line.note || '',
        }
      }
      return {
        value: line.prepared_quantity !== null ? line.prepared_quantity : line.quantity,
        weight_unit_id: null,
        note: line.note || '',
      }
    },
    // Cantidad de un producto común: entera si se cuenta (U, CAJA) y nunca mayor a lo pedido.
    quantityError(line) {
      if(line.weighable) return ''
      const value = this.forms[line.detail_id].value
      if(value === '' || value === null) return ''
      if(line.integer && !Number.isInteger(Number(value))) return 'En ' + line.unit_reference + ' va un número entero'
      if(Number(value) > Number(line.quantity)) return 'Máximo ' + this.formatQuantity(line.quantity) + ' ' + line.unit_reference + ' (lo pedido)'
      return ''
    },
    isShort(line) {
      if(line.weighable) return false
      const value = this.forms[line.detail_id].value
      return value !== '' && Number(value) < Number(line.quantity)
    },
    // Stock del sistema para lo pedido: alcanza (ok), no alcanza (low) o no hay (out).
    stockTone(line) {
      const available = Number(line.stock.available)
      if(available <= 0) return 'out'
      if(line.stock.converted !== false && available < Number(line.quantity)) return 'low'
      return 'ok'
    },
    stockTitle(line) {
      const base = 'Stock del sistema disponible para este pedido (incluye lo ya reservado al tomarlo)'
      const tone = this.stockTone(line)
      if(tone === 'out') return base + '. El sistema no tiene stock: si lo encontrás, avisá para corregir el inventario.'
      if(tone === 'low') return base + '. No alcanza para lo pedido según el sistema.'
      return base
    },
    // Faltante cargado aunque el sistema dice que hay más: hay que buscarlo o revisar el inventario.
    shortWithStock(line) {
      if(line.weighable || !line.stock || !line.stock.controlled || line.stock.converted === false) return false
      const value = this.forms[line.detail_id].value
      if(value === '' || value === null) return false
      return Number(value) < Number(line.quantity) && Number(line.stock.available) > Number(value)
    },
    confirmShortWithStock(line) {
      const form = this.forms[line.detail_id]
      const title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
        this.$createElement('AppIcon', { props: { name: 'search' } }), 'Faltante con stock en sistema',
      ])
      const message = line.description + ': cargás ' + this.formatQuantity(form.value) + ' de ' + this.formatQuantity(line.quantity) + ' ' + line.unit_reference +
        ', pero el sistema indica ' + this.formatQuantity(line.stock.available) + ' ' + line.stock.unit + ' disponibles. ¿Lo buscaste y confirmás el faltante?'
      return this.$bvModal.msgBoxConfirm(message, {
        title, size: 'sm', buttonSize: 'sm', okVariant: 'dark', okTitle: 'Sí, confirmar faltante', cancelTitle: 'Buscar de nuevo',
        footerClass: 'p-2', headerClass: 'app-confirm-header', hideHeaderClose: false, centered: true,
      })
    },
    // Avisa si el peso cargado se aleja mucho del estimado (típico: gramos cargados como kilos).
    weightWarning(line) {
      const form = this.forms[line.detail_id]
      if(!line.estimated_weight || form.value === '' || Number(form.value) <= 0) return ''
      const unit = this.weightUnits.find(item => item.id === form.weight_unit_id)
      const kilos = Number(form.value) / (unit && unit.reference.toUpperCase() === 'G' ? 1000 : 1)
      const ratio = kilos / line.estimated_weight
      if(ratio > 2 || ratio < 0.5) return 'Estimado ≈ ' + this.formatQuantity(line.estimated_weight) + ' kg. Revisá el peso o la unidad.'
      return ''
    },
    // Un peso muy lejos del estimado suele ser gramos cargados como kilos: se pide confirmación.
    confirmOddWeight(line) {
      const form = this.forms[line.detail_id]
      const unit = this.weightUnits.find(item => item.id === form.weight_unit_id)
      const title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
        this.$createElement('AppIcon', { props: { name: 'scale' } }), 'Revisar peso',
      ])
      const message = line.description + ': cargaste ' + this.formatQuantity(form.value) + ' ' + (unit ? unit.reference.toLowerCase() : '') +
        ' y lo esperado es ≈ ' + this.formatQuantity(line.estimated_weight) + ' kg. ¿El peso es correcto?'
      return this.$bvModal.msgBoxConfirm(message, {
        title, size: 'sm', buttonSize: 'sm', okVariant: 'dark', okTitle: 'Sí, guardar', cancelTitle: 'Corregir',
        footerClass: 'p-2', headerClass: 'app-confirm-header', hideHeaderClose: false, centered: true,
      })
    },
    isDirty(line) {
      const form = this.forms[line.detail_id]
      const saved = this.formFor(line)
      const sameValue = form.value === '' || saved.value === ''
        ? String(form.value) === String(saved.value)
        : Number(form.value) === Number(saved.value)
      return !sameValue ||
        Number(form.weight_unit_id) !== Number(saved.weight_unit_id) ||
        (form.note || '') !== (saved.note || '')
    },
    lineClass(line, type) {
      if(type !== 'row' || !line) return ''
      return [line.weighable ? 'cut-order__line--weighable' : '', this.controlled ? 'cut-order__line--locked' : ''].join(' ')
    },
    // Guarda solo si cambió algo. Lo que no se toca se toma con la cantidad pedida al controlar el pedido.
    async save(line) {
      if(this.locked || this.saving[line.detail_id]) return false
      if(!this.isDirty(line)) return true
      const form = this.forms[line.detail_id]
      if(form.value === '' || form.value === null) {
        if(!line.weighable) this.$set(this.errors, line.detail_id, 'Ingresá la cantidad (0 si no hay)')
        return line.weighable
      }
      if(this.quantityError(line)) {
        this.focusLine(line)
        return false
      }
      const ask = line.weighable && this.weightWarning(line) ? this.confirmOddWeight
        : (this.shortWithStock(line) ? this.confirmShortWithStock : null)
      if(ask) {
        // Al abrir el diálogo el campo pierde el foco y dispara otro guardado: se ignora.
        if(this.confirming) return false
        this.confirming = true
        const ok = await ask(line)
        this.confirming = false
        if(!ok) {
          this.focusLine(line)
          return false
        }
      }
      this.$set(this.saving, line.detail_id, true)
      this.$set(this.errors, line.detail_id, '')
      try {
        const payload = line.weighable
          ? { measured_weight: form.value, weight_unit_id: form.weight_unit_id, note: form.note }
          : { prepared_quantity: form.value, note: form.note }
        const response = await serviceAPI.prepararDetalle(this.cut.id, line.detail_id, payload)
        const index = this.lines.findIndex(item => item.detail_id === line.detail_id)
        const updated = response.data.line
        this.$set(this.lines, index, updated)
        this.forms[line.detail_id] = this.formFor(updated)
        this.$emit('updated', response.data)
        return true
      } catch(error) {
        this.$set(this.errors, line.detail_id, Error.showError(error))
        return false
      } finally {
        this.$set(this.saving, line.detail_id, false)
      }
    },
    focusLine(line) {
      this.$nextTick(() => {
        const ref = this.$refs['input-' + line.detail_id]
        const input = Array.isArray(ref) ? ref[0] : ref
        if(input) input.focus()
      })
    },
    // Enter guarda (si cambió) y pasa al siguiente renglón: se recorre el pedido con el teclado.
    async saveAndNext(line) {
      const saved = await this.save(line)
      if(!saved) return
      const index = this.lines.findIndex(item => item.detail_id === line.detail_id)
      const next = this.lines[index + 1]
      if(next) this.focusLine(next)
    },
    // Antes de controlar se guardan los cambios que todavía estén en los campos.
    async control() {
      if(this.lines) {
        for(const line of this.lines) {
          if(this.isDirty(line) && !(await this.save(line))) {
            this.focusLine(line)
            return
          }
        }
      }
      this.$emit('control', this.order)
    },
    formatQuantity(value) {
      return Number(value).toLocaleString('es-AR', { maximumFractionDigits: 3 })
    },
  },
}
</script>

<style scoped>
.cut-order { border-bottom: 1px solid var(--app-color-border, #dee2e6); background: var(--app-color-surface, #fff); box-shadow: inset 3px 0 transparent; }
.cut-order--open { background: var(--app-color-surface-subtle, #f7f8f9); }
.cut-order--pending { box-shadow: inset 3px 0 var(--app-color-border-strong, #c5ccd3); }
.cut-order--partial { box-shadow: inset 3px 0 var(--app-color-warning, #e0a100); }
.cut-order--ready, .cut-order--invoiced { box-shadow: inset 3px 0 var(--app-color-success, #1e7b4a); }
.cut-order--running { box-shadow: inset 3px 0 var(--app-color-primary, #2b5f8a); }
.cut-order--error { box-shadow: inset 3px 0 var(--app-color-danger, #c0392b); }

.cut-order__head { display: flex; align-items: center; gap: 0.55rem; min-height: 2.7rem; padding: 0.35rem 0.6rem; cursor: pointer; }
.cut-order__head:hover { background: var(--app-color-surface-subtle, #f7f8f9); }
.cut-order__head:focus { outline: 2px solid var(--app-color-primary-soft, #dbe7f3); outline-offset: -2px; }
.cut-order__chevron { flex-shrink: 0; color: var(--app-color-text-muted, #687684); }
.cut-order__state { display: inline-flex; flex-shrink: 0; font-size: 1.1rem; }
.cut-order--pending .cut-order__state { color: var(--app-color-text-muted, #9aa5b1); }
.cut-order--partial .cut-order__state { color: var(--app-color-warning, #e0a100); }
.cut-order--ready .cut-order__state, .cut-order--invoiced .cut-order__state { color: var(--app-color-success, #1e7b4a); }
.cut-order--running .cut-order__state { color: var(--app-color-primary, #2b5f8a); }
.cut-order--error .cut-order__state { color: var(--app-color-danger, #c0392b); }
.cut-order__identity { display: flex; align-items: center; gap: 0.45rem; flex: 1 1 auto; min-width: 0; }
.cut-order__number { flex-shrink: 0; font-weight: 700; color: var(--app-color-text, #1c2833); }
.cut-order__customer { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--app-color-text, #1c2833); }
.cut-order__status { display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: 0.4rem; }
.cut-order__control { padding: 0.05rem 0.5rem; font-size: 0.75rem; }
.cut-order__actions { flex-shrink: 0; width: 28px; }
.cut-order__tag { display: inline-flex; align-items: center; gap: 0.2rem; padding: 0.05rem 0.4rem; border: 1px solid var(--app-color-border, #dee2e6); border-radius: 0.3rem; font-size: 0.7rem; font-weight: 600; white-space: nowrap; color: var(--app-color-text-secondary, #5d6872); background: var(--app-color-surface, #fff); }
.cut-order__tag .app-icon { font-size: 0.85rem; }
.cut-order__tag--warning { color: #8a5a00; border-color: var(--app-color-warning-border, #f0d48a); background: var(--app-color-warning-soft, #fff8e6); }
.cut-order__tag--danger { color: var(--app-color-danger, #b42318); border-color: var(--app-color-danger-border, #f3c2bd); background: var(--app-color-danger-soft, #fdf0ef); }
.cut-order__tag--success { color: var(--app-color-success, #1e7b4a); border-color: var(--app-color-success-border, #b7e0c8); background: var(--app-color-success-soft, #eef8f2); }
.cut-order__tag--info { color: var(--app-color-primary, #2b5f8a); border-color: var(--app-color-info-border, #c4d9ec); background: var(--app-color-primary-soft, #eef4fa); }
.cut-order__error { padding: 0 0.75rem 0.45rem 3.6rem; font-size: 0.78rem; color: var(--app-color-danger, #b42318); }

.cut-order__body { padding: 0 0.75rem 0.75rem 3.6rem; }
.cut-order__loading { padding: 0.5rem 0; font-size: 0.8rem; color: var(--app-color-text-muted, #687684); }
.cut-order__weigh-hint { display: flex; align-items: flex-start; gap: 0.5rem; margin-bottom: 0.5rem; padding: 0.5rem 0.65rem; border: 1px solid var(--app-color-warning-border, #f0d48a); border-radius: 0.4rem; font-size: 0.78rem; color: #6b4600; background: var(--app-color-warning-soft, #fff8e6); }
.cut-order__weigh-hint .app-icon { font-size: 1.15rem; color: #8a5a00; }
.cut-order__lines { background: var(--app-color-surface, #fff); }
.cut-order__lines >>> td { vertical-align: middle; }
.cut-order__lines >>> .cut-order__col-qty { width: 110px; padding-right: 1rem; white-space: nowrap; }
.cut-order__lines >>> .cut-order__col-prepared { width: 190px; }
.cut-order__lines >>> .cut-order__col-state { width: 44px; white-space: nowrap; }
.cut-order__lines >>> tr.cut-order__line--weighable td { background: var(--app-color-warning-soft, #fffaf0); }
.cut-order__lines >>> tr.cut-order__line--locked td { color: var(--app-color-text-secondary, #5d6872); }
.cut-order__code { margin-right: 0.35rem; font-size: 0.75rem; color: var(--app-color-text-muted, #687684); }
.cut-order__estimate { display: block; color: #8a5a00; }
.cut-order__stock { display: inline-flex; align-items: center; gap: 0.15rem; color: var(--app-color-text-muted, #687684); white-space: nowrap; }
.cut-order__stock .app-icon { font-size: 0.85rem; }
.cut-order__stock--low { color: #8a5a00; font-weight: 600; }
.cut-order__stock--out { color: var(--app-color-danger, #b42318); font-weight: 600; }
.cut-order__field-warning--stock { color: #8a5a00; }
.cut-order__input { display: flex; align-items: center; gap: 0.35rem; }
.cut-order__input .form-control { width: 96px; text-align: right; -moz-appearance: textfield; }
.cut-order__input .form-control::-webkit-outer-spin-button,
.cut-order__input .form-control::-webkit-inner-spin-button { margin: 0; -webkit-appearance: none; }
.cut-order__input--short { border-color: var(--app-color-danger, #b42318); color: var(--app-color-danger, #b42318); }
.cut-order__input--weigh .form-control { font-weight: 600; border-color: var(--app-color-warning-border, #f0d48a); background: #fffdf5; }
.cut-order__input--weigh .form-control:focus { border-color: #d39e00; box-shadow: 0 0 0 0.15rem rgba(224, 161, 0, 0.2); }
.cut-order__input .cut-order__unit { width: 58px; padding-left: 0.4rem; border-color: var(--app-color-warning-border, #f0d48a); }
.cut-order__unit-label { font-size: 0.78rem; color: var(--app-color-text-muted, #687684); }
.cut-order__field-warning { display: block; max-width: 190px; margin-top: 0.2rem; line-height: 1.2; color: var(--app-color-danger, #b42318); }
.cut-order__note { margin-top: 0.3rem; max-width: 360px; }
.cut-order__note-btn { padding: 0; color: var(--app-color-text-muted, #687684); }
.cut-order__footer { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; flex-wrap: wrap; margin-top: 0.5rem; padding: 0.5rem 0.65rem; border: 1px solid var(--app-color-border, #dee2e6); border-radius: 0.4rem; font-size: 0.78rem; color: var(--app-color-text-secondary, #5d6872); background: var(--app-color-surface, #fff); }
.cut-order__footer--done { color: var(--app-color-success, #1e7b4a); border-color: var(--app-color-success-border, #b7e0c8); background: var(--app-color-success-soft, #eef8f2); }
.cut-order__footer .app-icon { vertical-align: -0.15em; }
@media (max-width: 767.98px) {
  .cut-order__head { flex-wrap: wrap; }
  .cut-order__identity { flex-basis: calc(100% - 5.5rem); }
  .cut-order__status { order: 3; width: 100%; justify-content: flex-start; padding-left: 2.9rem; }
  .cut-order__body, .cut-order__error { padding-left: 0.75rem; }
  .cut-order__lines >>> .cut-order__col-prepared, .cut-order__lines >>> .cut-order__col-qty { width: auto; }
}
</style>
